import React, { useEffect, useRef, useState } from 'react';
import { FiservCardBrand, getFiservCardInfoFromToken } from './fiservTokenUtils';
import { useAppDispatch } from '../../../redux/store';
import { getFiservCardDetails } from '../../../redux/services/paymentsService';
import {
  capitalizeWordsInSentence,
  CardType,
  appDelay,
  FiservCardDetails,
} from '@dill/dill-shared';
import { Spinner } from 'phosphor-react';
import { CARD_CONNECT_BASE_URL } from '../../../utils/constants';
import AmexCard from '../../../assets/images/amex-card.png';
import VisaCard from '../../../assets/images/visa-card.png';
import DiscoverCard from '../../../assets/images/discover-card.png';
import MastercardCard from '../../../assets/images/mastercard-card.png';
import DinersCard from '../../../assets/images/diners-card.png';
import JCBCard from '../../../assets/images/jcb-card.png';

export type FiservCardResult =
  | { success: true; data: FiservCardDetails }
  | { success: false; error: string };

export interface AppFiservCardInputProps {
  isWarnCardToBeSaved?: boolean;
  onResult?: (result: FiservCardResult) => void;
}

function AppFiservCardInput({ onResult, isWarnCardToBeSaved = false }: AppFiservCardInputProps) {
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [isCardValidated, setIsCardValidated] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [fiservCardData, setFiservCardData] = useState<FiservCardDetails | null>(null);
  const [zipcode, setZipcode] = useState<string>('');

  // Map card brands to their respective images
  const getCardBrandImage = (brand: FiservCardBrand): string | null => {
    switch (brand) {
      case 'AMEX':
        return AmexCard;
      case 'VISA':
        return VisaCard;
      case 'MASTERCARD':
        return MastercardCard;
      case 'DISCOVER':
        return DiscoverCard;
      case 'DINERS':
        return DinersCard;
      case 'JCB':
        return JCBCard;
      default:
        return null;
    }
  };

  // Validate US ZIP code format (5 digits or 5+4 digits)
  const validateZipCode = (zip: string): boolean => {
    if (!zip) {
      setValidationError('ZIP code is required');
      return false;
    }

    // Allow 5-digit format (12345) or ZIP+4 format (12345-6789)
    const zipRegex = /^\d{5}(-\d{4})?$/;

    if (!zipRegex.test(zip)) {
      setValidationError('Invalid ZIP code format. Use 5 digits (12345) or ZIP+4 (12345-6789)');
      return false;
    }

    setValidationError(null);
    return true;
  };

  useEffect(() => {
    setFiservCardData(null);
    setZipcode('');
    setValidationError('');
    setError('');
    return () => {};
  }, []);

  useEffect(() => {
    if (
      fiservCardData &&
      fiservCardData.brand &&
      fiservCardData.cardName &&
      fiservCardData.cardType &&
      fiservCardData.expiry &&
      fiservCardData.last4 &&
      fiservCardData.token &&
      fiservCardData?.zipCode
    ) {
      // Trigger the onResult callback with success and card data
      onResult?.({
        success: true,
        data: fiservCardData,
      });
    }
    return () => {};
  }, [fiservCardData]);

  useEffect(() => {
    // Listen for messages from the iFrame to receive the token
    const handleMessage = async (event: any) => {
      // Ensure the message originates from the expected domain for security
      if (event.origin === CARD_CONNECT_BASE_URL) {
        // console.log({ event: event?.data });
        try {
          const data = JSON.parse(event.data);

          if (data.validationError) {
            const errorMsg = data.validationError;
            setValidationError(errorMsg);
            setError(null);
            setIsValidating(false);
            setFiservCardData(null);
            setIsCardValidated(false);
            setZipcode('');
            onResult?.({ success: false, error: errorMsg });
            return;
          }

          // Handle enhanced response
          if (data.errorCode && data.errorCode !== '0') {
            const errorMsg = data.errorMessage || 'An error occurred during tokenization';
            setError(errorMsg);
            setValidationError(null);
            setFiservCardData(null);
            setIsCardValidated(false);
            setZipcode('');
            onResult?.({ success: false, error: errorMsg });
            return;
          }

          // Handle successful tokenization
          const token = data.message || data.token;
          const expiry = data.expiry;

          if (token) {
            // Signal that validation is complete
            setError(null);
            setValidationError(null);
            setIsValidating(true);
            setFiservCardData(null);
            const cardDetailsResponse = await dispatch(
              getFiservCardDetails({ fiservCardToken: token })
            );
            setIsValidating(false);
            let cardTypeFetched: CardType = 'UNKNOWN';
            if (
              cardDetailsResponse.type === 'payments/getFiservCardDetails/fulfilled' &&
              cardDetailsResponse.payload
            ) {
              const payload: any = cardDetailsResponse.payload;
              if (payload && payload.data) {
                cardTypeFetched = payload.data.cardType;
              }
            }
            if (
              !cardDetailsResponse.payload ||
              cardDetailsResponse.type === 'payments/getFiservCardDetails/rejected'
            ) {
              let errorMsg = 'Error validating card. Failed to get card details ';
              if (cardDetailsResponse?.payload) {
                errorMsg = `Error validating card. ${cardDetailsResponse?.payload}`;
              }
              setError(errorMsg);
              setIsValidating(false);
              setFiservCardData(null);
              setIsCardValidated(false);
              setZipcode('');
              onResult?.({ success: false, error: errorMsg });
              return;
            }
            // Extract card type and last 4 digits from token
            const extractedCardInfo = getFiservCardInfoFromToken(token);
            const isValid = validateZipCode(zipcode);
            setIsCardValidated(true);

            const cardData: FiservCardDetails = {
              token,
              brand: extractedCardInfo.brand,
              last4: extractedCardInfo.last4,
              expiry: expiry,
              zipCode: isValid ? zipcode : '',
              cardType: cardTypeFetched,
              cardName: `${capitalizeWordsInSentence(extractedCardInfo.brand.toLowerCase())} ****${
                extractedCardInfo.last4
              }`,
            };
            setFiservCardData(cardData);
          }
        } catch (err) {
          console.error('Error parsing iframe message:', err);
          const errorMsg = 'Failed to process card information';
          setError(errorMsg);
          setIsValidating(false);
          setFiservCardData(null);
          setIsCardValidated(false);
          setZipcode('');
          onResult?.({ success: false, error: errorMsg });
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onResult]);

  // Build iframe URL with enhanced parameters
  // Using specific IDs from the iframe HTML structure
  const customCSS = `
    body {
      margin: 0;
      background: #fff;
      font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
    }

    label {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    input,
    select {
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background-color: #ffffff;
      color: #111827;
      padding: 8px 10px;
      font-size: 16px;
      transition: all 0.2s ease;
      box-sizing: border-box;
      margin-bottom: 4px;
    }

    input:focus,
    select:focus {
      border-color: #3b82f6;
      background-color: #ffffff;
      outline: 0;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    input:hover:not(:focus),
    select:hover:not(:focus) {
      border-color: #9ca3af;
    }

    select {
      appearance: none;
      background-image: url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3e%3cpolyline points="6 9 12 15 18 9"%3e%3c/polyline%3e%3c/svg%3e');
      background-repeat: no-repeat;
      background-position: right 12px center;
      background-size: 14px;
    }
    #cccvvlabel {
       margin-left: 15px;
      }
    #ccnumfield {
       width: 250px;
      }

    .error {
      border-color: #ef4444 !important;
      background-color: #fef2f2 !important;
    }

    .error:focus {
      border-color: #dc2626 !important;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
    }

  `;

  const iframeParams = new URLSearchParams({
    useexpiry: 'true',
    usecvv: 'true',
    usemonthnames: 'true',
    formatinput: 'true',
    invalidinputevent: 'true',
    enhancedresponse: 'true',
    tokenizewheninactive: 'true',
    invalidcreditcardevent: 'true',
    invalidcvvevent: 'true',
    invalidexpiryevent: 'true',
    orientation: 'horizontal',
    cardlabel: 'Card Number',
    expirylabel: 'Expiration Date',
    cvvlabel: 'CVV',
    css: customCSS,
  });

  // Replace + with %20 for proper space encoding in labels
  const iframeUrl = `${CARD_CONNECT_BASE_URL}/itoke/ajax-tokenizer.html?${iframeParams
    .toString()
    .replace(/\+/g, '%20')}`;

  return (
    <div className="flex py-4 px-6 w-full">
      <form
        name="tokenform"
        id="tokenform"
        className="flex flex-col w-full"
        onSubmit={(e) => e.preventDefault()}>
        <div className="bg-white rounded-lg mb-2 flex h-fit w-full relative flex-col">
          {isIframeLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white rounded-lg z-10">
              <div className="flex flex-col items-center">
                <Spinner className="animate-spin" color="#3762FB" size={32} />
                <p className="mt-2 text-sm text-gray-600">Loading secure form...</p>
              </div>
            </div>
          )}
          <iframe
            id="tokenFrame"
            name="tokenFrame"
            src={iframeUrl}
            ref={iframeRef}
            onLoad={async () => {
              await appDelay(500);
              setIsIframeLoading(false);
            }}
            style={{ width: '100%', border: 'none', height: '130px' }}
            title="Secure card information form"></iframe>
          {!isIframeLoading && isCardValidated && (
            <div className="mb-3">
              {fiservCardData?.brand && getCardBrandImage(fiservCardData.brand) && (
                <div className="mb-3 flex items-center gap-2">
                  <img
                    src={getCardBrandImage(fiservCardData.brand) || ''}
                    alt={`${fiservCardData.brand} card`}
                    className="h-8 w-auto object-contain"
                  />
                  <span className="text-sm font-semibold text-gray-700">
                    {fiservCardData.cardName}
                  </span>
                </div>
              )}
              <label htmlFor="zipcode" className="block text-sm font-semibold text-gray-700 mb-1">
                ZIP Code
              </label>

              <input
                id="zipcode"
                type="text"
                value={zipcode}
                onChange={(e) => {
                  setZipcode(e.target.value);
                  // Clear validation error when user starts typing
                  if (validationError) {
                    setValidationError(null);
                  }
                }}
                maxLength={10}
                onBlur={() => {
                  const isValid = validateZipCode(zipcode);
                  if (isValid && fiservCardData && !fiservCardData?.zipCode) {
                    setFiservCardData({ ...fiservCardData, zipCode: zipcode });
                  }
                }}
                placeholder="12345 or 12345-6789"
                className={`w-48 border rounded-lg px-3 py-2 text-base focus:ring-2 focus:outline-none transition-all ${
                  validationError && validationError.toLowerCase().includes('zip')
                    ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-100'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100 hover:border-gray-400'
                }`}
              />
            </div>
          )}
        </div>

        {validationError && <p className="text-sm text-yellow-500">{validationError}</p>}
        {isWarnCardToBeSaved && (
          <p className="text-sm text-yellow-500">Card details will be saved for future payments</p>
        )}

        {error && <p className="text-sm text-red-800">{error}</p>}
        {isValidating && (
          <div className="flex items-center">
            <Spinner className="animate-spin" color="#3762FB" size={24} />
            <p className="ml-2 text-sm">Validating card</p>
          </div>
        )}
      </form>
    </div>
  );
}

export default AppFiservCardInput;
